import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import {
  Box,
  createStyles,
  debounce,
  Grid,
  makeStyles,
  TextField,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import type { IAddRepairJob, IScannedRepair } from './EstimatorInterfaces';
import AllRepairJobs from './AllRepairJobs';
import EstimatorRepairCard from './EstimatorRepairCard';
import ClarifyingInformation from './ClarifyingInformation';
import MissingInformationDialog from './MissingInformationDialog';
import EstimatorUpsertRepairJobs from './EstimatorUpsertRepairJob';
import EstimatorCommentCards from './EstimatorCommentCards';
import EditRepairJobDialog from './EditRepairJobDialog';
import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';
import NavigationDropdown from '../navigation/NavigationDropdown';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  estimatorCard: {
    height: 180,
  },
  estimatorSvg: {
    height: 24,
    width: 24,
    marginBottom: 7,
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  estimatorRadioButtons: {
    '& .MuiFormGroup-root, .MuiFormControlLabel-root, label': {
      marginBottom: '0',
    },
  },
}));

const Estimator: React.FC = () => {
  const [cookies, setCookie] = useCookies<string, { estimatorNextPage?:string } >(['estimatorNextPage']);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [repairId, setRepairId] = React.useState<string>();
  const [showMissingInfoDialog, setShowMissingInfoDialog] = React.useState<boolean>(false);
  const [showEditRepairJobDialog, setShowEditRepairJobDialog] = React.useState<boolean>(false);
  const [openRepairProcessing, setOpenRepairProcessing] = React.useState<boolean>(false);
  const [forceReset, setForceReset] = React.useState(0);
  const [editJob, setEditJob] = React.useState<IAddRepairJob | undefined>();
  const [editJobId, setEditJobId] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const navPages = ['Current', 'Photography', 'Marking'];
  const navigate = useNavigate();

  const scanBarcode = debounce((scannedBarcode: string, outScan = false) => {
    // if there's no content
    if (!scannedBarcode) {
      return;
    }
    if (outScan) {
      axios.post('/scanner/LogManualScan', { ScanType: 'Out', ScanBody: scannedBarcode, Location: 'Estimator' })
        .catch((er: AxiosError) => {
          enqueueSnackbar(er?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      setOpenRepairProcessing(true);
      axios.get<{ repairId:string }>('/estimator/scanRepair', {
        params: {
          barcode: scannedBarcode,
        },
      })
        .then((response) => {
          setRepairId(response.data.repairId);
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
          setOpenRepairProcessing(false);
        });
    }
  }, 500);

  let barcodeBuffer = '';

  const handlePageSelectedChange = (page: string) => {
    setCookie('estimatorNextPage', page);
  };

  const handleRoute = (barcode:string) => {
    switch (cookies.estimatorNextPage) {
      case 'Current': {
        break;
      }
      case 'Photography': {
        navigate(`/photograph-repair/${barcode}`);
        break;
      }
      case 'Marking': {
        navigate(`/mark-repair/${barcode}`);
        break;
      }
      default: {
        enqueueSnackbar(cookies.estimatorNextPage ? `Invalid barcode action page selected: ${cookies.estimatorNextPage}` : 'No next page action selected', { variant: 'error' });
        break;
      }
    }
  };

  const { barcodeParam } = useParams();

  // if a barcode came in as a url query
  React.useEffect(() => {
    if (barcodeParam) {
      scanBarcode(barcodeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetcher = async (url: string, currentRepairId: string) => {
    const res = await axios.get<IScannedRepair>(url, {
      params: {
        repairId: currentRepairId,
      },
    })
      .then((response) => response.data)
      .catch((e: AxiosError) => {
        if (e.response?.status === 404) {
          enqueueSnackbar(`There was the following error when fetching the repair: ${JSON.stringify(e.response.data)}`, { persist: false, variant: 'error' });
        } else {
          enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
        }
      })
      .finally(() => { setOpenRepairProcessing(false); });
    return res;
  };

  const { data, mutate } = useSWR(repairId
    ? ['/estimator/getRepair', repairId]
    : null,
  (url, repairIdParameter) => fetcher(url, repairIdParameter));

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);
        if (cookies.estimatorNextPage === 'Current' || data === undefined) {
          scanBarcode(barcodeBuffer.replace('{*|}', ''));
        } else {
          handleRoute(barcodeBuffer.replace('{*|}', ''));
        }
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        scanBarcode(barcodeBuffer.replace('<>??<>', ''), true);
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  function ScanBarcode() {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to start the estimating process.'}</Alert>
        </Grid>
        <Grid item>
          {openRepairProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onKeyPress={
                (event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    scanBarcode((event.target as HTMLInputElement).value);
                  }
                }
                }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  if (!data) {
    return (
      <ScanBarcode />
    );
  }

  const handleAddRepairJob = async (repairJobParam: IAddRepairJob) => {
    setLoading(true);
    const response = await axios.post('/estimator/addRepairJob', repairJobParam)
      .then((res) => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        const newReset = forceReset;
        setForceReset((newReset + 1) % 2);
        setLoading(false);
        enqueueSnackbar('Successfully added repair job', { variant: 'success' });
        return res;
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        setLoading(false);
        return error;
      });
    return response;
  };

  const handleDeleteRepairItemJob = (jobId: string) => {
    axios.post('/estimator/deleteRepairJob', { RepairId: repairId, JobId: jobId })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        enqueueSnackbar('Successfully deleted repair job', { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const handleEditRepairJob = async (jobId: string, repairJobParam: IAddRepairJob) => {
    try {
      setLoading(true);
      const response = await axios.post('/estimator/deleteRepairJob', { RepairId: repairId, JobId: jobId })
        .then(() => {
          // eslint-disable-next-line no-void
          void handleAddRepairJob(repairJobParam);
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
      return response;
      // hide edit repair job modal and clear state
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(JSON.stringify(error), { persist: false, variant: 'error' });
      return error;
    }
  };

  const handleOpenEditRepairItemJob = (jobId: string) => {
    axios.get<IAddRepairJob>('estimator/getRepairItemJobForEdit', {
      params: {
        jobId,
        barcode: data.barcode,
      },
    })
      .then((response) => {
        setEditJob(response.data);
        setEditJobId(jobId);
        setShowEditRepairJobDialog(true);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.message, { persist: false, variant: 'error' });
      });
  };

  const handleMissingInfo = () => {
    setShowMissingInfoDialog(true);
  };

  const handleMissingInfoDialogClose = (repairIdParameter: string, details?: string) => {
    // if modal was closed with details to save
    if (details) {
      // update details
      setLoading(true);
      axios.post('/estimator/updateMissingInfoDetails', {
        repairId: repairIdParameter,
        missingInfoDetails: details,
      })
        .then(() => {
          enqueueSnackbar(`missing info, with details: ${details}`, { variant: 'success' });
          // close dialog
          setShowMissingInfoDialog(false);
          setRepairId('');
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // close dialog
      setShowMissingInfoDialog(false);
    }
  };

  const handleEditRepairJobDialogClose = () => {
    setShowEditRepairJobDialog(false);
    setEditJob(undefined);
    setEditJobId('');
  };

  return (
    <Box className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid item xs={8} container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">{'Search and Attach Repair Jobs to the Repair Item(s).'}</Alert>
          </Grid>
          <Grid item xs={6}>
            <EstimatorRepairCard
              barcode={data.barcode}
              isHighPriority={data.isHighPriority}
            />
          </Grid>
          <Grid item xs={6}>
            <ClarifyingInformation
              barcode={data.barcode}
              handleMissingInfo={handleMissingInfo}
            />
          </Grid>
          <Grid item xs={12}>
            <EstimatorUpsertRepairJobs
              key={forceReset}
              barcode={data.barcode}
              handleAddRepairJob={handleAddRepairJob}
              repairItems={data.repairItems}
              loading={loading && !showEditRepairJobDialog}
            />
          </Grid>
          <Grid item xs={12}>
            <AllRepairJobs
              repairItemJobs={data.repairItemJobs}
              handleDeleteRepairItemJob={handleDeleteRepairItemJob}
              handleOpenEditRepairItemJob={handleOpenEditRepairItemJob}
            />
          </Grid>
          <Grid>
            <NavigationDropdown
              pageSelected={cookies.estimatorNextPage}
              pages={navPages}
              handlePageChange={handlePageSelectedChange}
            />
          </Grid>
        </Grid>
        <Grid>
          <Box pl={2}>
            <Divider orientation="vertical" />
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box p={2}>
            <EstimatorCommentCards
              repairId={data.repairId}
              repairTableLength={data.repairItemJobs.length}
              allowNewComments
            />
          </Box>
        </Grid>
      </Grid>

      <MissingInformationDialog
        repairId={data.repairId}
        barcode={data.barcode}
        showModal={showMissingInfoDialog}
        onClose={handleMissingInfoDialogClose}
        isFetching={loading}
      />
      <EditRepairJobDialog
        key={forceReset}
        barcode={data.barcode}
        editJob={editJob}
        editJobId={editJobId}
        handleEditRepairJob={handleEditRepairJob}
        repairItems={data.repairItems}
        onClose={handleEditRepairJobDialogClose}
        showModal={showEditRepairJobDialog}
        loading={loading}
      />
    </Box>
  );
};

export default Estimator;
